// @ts-nocheck
import { capitalize, flatten } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { KATable, ReportFilters, useQueryFetch } from 'sonata-react-components';
import { Formatters } from 'sonata-static-storage/lib/helpers/generalFormatters.helpers';
import { getFormattedFilterGroups } from 'sonata-static-storage/lib/helpers/stats.helpers';
import { statsPrefs } from 'sonata-static-storage/lib/samples/statsPreferences.samples';

import { getPaginatedReportingStats } from '@/modules/reporting/reporting.resources';
import { getCountriesWithEvents } from '@/store/selectors/reporting.selectors';
import {
  DISABLED_METRICS,
  getStatsTableVariableFilters,
  getTableOutputMetrics,
  StatsColumnsDictionary
} from '@/ts-helpers/stats.helper';
import { DataFeesTooltip } from '@/ts-modules/reporting/DataFeesTooltip';
import { getCartographyDictionary } from '@/ts-resources/reporting.resources';

import './statsTable.scss';

import type { GeoDictionaryTagsConfig } from 'mercator-cartography-api-client/lib/interfaces/geoDictionary.types';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { StaticReports } from '@/ts-types/reporting.types';

type TableType = 'footfallAttribution' | 'stats' | 'viewability';

type Props = {
  campaigns: Record<string, any>[];
  country: string;
  currency: {
    base: string;
    current: string;
    info: BaseCurrencyInterface;
  };
  date: {
    begin: Date;
    end: Date;
    timezone: string;
  };
  defaultGroups?: string[];
  filters: Record<string, any>;
  includeDateGrouping?: boolean;
  onColumnVisibilityChange: (column: string, visible: boolean) => void;
  onGroupsChange: (groups: string[]) => void;
  opts: Record<string, any>;
  session: Record<string, any>;
  staticMode: boolean;
  staticReports: StaticReports;
  statsColumns: Record<string, any>[];
  type: TableType;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

const TABLE_TYPE_ID: Record<TableType, string> = {
  footfallAttribution: 'reportingFootfallAttributionTable',
  stats: 'reportingStatsTable',
  viewability: 'reportingViewabilityTable'
};

const defaultStyles: React.CSSProperties = {
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

function statsTableCartographyKey(countries: string[]) {
  return `statsTable-cartography-${countries.join('|')}`;
}

function getWithSuffix(id: string): string {
  const suffix = 'Id';
  return `${id}${suffix}`;
}

const DATE_DIMENSIONS = ['hour', 'day', 'week', 'month', 'year'];
const TIME_PART_DIMENSIONS = ['hod', 'dow', 'mod', 'mow'];
const DATE = [...DATE_DIMENSIONS, ...TIME_PART_DIMENSIONS];

const isMetricEnabled = (metric: string) => !DISABLED_METRICS.includes(metric) && metric !== 'sdate';

type State = {
  groups: string[];
  isFirstRender: boolean;
  visibleColumns: Record<string, boolean>;
};

function StatsTable({
  campaigns,
  country,
  currency,
  date,
  defaultGroups = [],
  filters,
  includeDateGrouping = false,
  onColumnVisibilityChange,
  onGroupsChange,
  opts,
  session,
  staticMode,
  staticReports,
  statsColumns,
  type,
  user,
  userRoles
}: Props) {
  const DEFAULT_STATIC_MODE_DIMENSION = 'day';
  const DEFAULT_REPORT_NAME = 'Default Report';
  const DIMENSIONS_TO_SELECT_WHEN_STATIC = 1;

  const capitalizeFirstLetter = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
  const countries: string[] = useSelector(getCountriesWithEvents);
  const [{ groups, isFirstRender, visibleColumns }, setState] = useState<State>({
    groups: defaultGroups,
    isFirstRender: true,
    visibleColumns: statsColumns.reduce(
      (prev, current) => ({
        ...prev,
        [current.prop]: current.visible
      }),
      {}
    )
  });
  const { formatMessage } = useIntl();

  if (isFirstRender) {
    setState(prevState => ({
      ...prevState,
      isFirstRender: false
    }));
  }

  const { data: cartographyDictionaryTagConfigsData } = useQueryFetch<GeoDictionaryTagsConfig[]>(
    statsTableCartographyKey(countries),
    async () => {
      const demographyCountry = await getCartographyDictionary(countries);
      return flatten(demographyCountry.map(cConfig => cConfig.tagsConfig as GeoDictionaryTagsConfig));
    },
    {
      enabled: countries.length === 1,
      placeholderData: [
        {
          id: 'level0',
          name: 'Country'
        }
      ]
    }
  );

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'campaignStats') {
        const storage = localStorage.getItem('campaignStats')
          ? JSON.parse(localStorage.getItem('campaignStats')!)
          : null;
        const columnTableVisible = storage.find((i: any) => i[`${type}VisibleHeaders`])?.[`${type}VisibleHeaders`];
        const userColumnTableVisible = columnTableVisible[session.userFrom || session.user || user._id!];
        if (Array.isArray(userColumnTableVisible)) {
          setState(prevState => {
            return {
              ...prevState,
              visibleColumns: Object.keys(prevState.visibleColumns).reduce(
                (prev, columnKey) => ({
                  ...prev,
                  [columnKey]: userColumnTableVisible.includes(columnKey)
                }),
                {}
              )
            };
          });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange, true);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const getTimeOptions = () => {
    if (!includeDateGrouping || staticMode) {
      return [];
    }
    return DATE.map(d => ({
      id: d,
      label: formatMessage({
        defaultMessage: capitalize(d),
        id: `reporting.cpy${capitalize(d)}`
      })
    }));
  };

  const groupOptions = getStatsTableVariableFilters(
    type,
    user,
    userRoles,
    campaigns,
    cartographyDictionaryTagConfigsData
  ).map(i => ({
    id: getFormattedFilterGroups([i])[0].replace('..', '.'),
    label: i.startsWith('cartoPyramid')
      ? `${formatMessage({
          defaultMessage: 'Cartography - ',
          id: 'reporting.cpyFiltercartoPyramid'
        })} ${cartographyDictionaryTagConfigsData?.find(op => i.includes(op.id!))?.name || i}`
      : formatMessage({
          defaultMessage: capitalize(i),
          id: `reporting.cpyTable${capitalizeFirstLetter(i)}`
        })
  }));

  const getGroupOptions = () => {
    if (staticMode) {
      return staticReports.map(sr => ({
        id: sr.id,
        label: sr.name
      }));
    }
    return getStatsTableVariableFilters(type, user, userRoles, campaigns, cartographyDictionaryTagConfigsData).map(
      i => ({
        id: getFormattedFilterGroups([i])[0].replace('..', '.'),
        label: i.startsWith('cartoPyramid')
          ? `${formatMessage({
              defaultMessage: 'Cartography - ',
              id: 'reporting.cpyFiltercartoPyramid'
            })} ${cartographyDictionaryTagConfigsData?.find(op => i.includes(op.id!))?.name || i}`
          : formatMessage({
              defaultMessage: capitalize(i),
              id: `reporting.cpyTable${capitalizeFirstLetter(i)}`
            })
      })
    );
  };

  const columns: Record<string, any>[] = [];

  if (statsColumns.length) {
    const statsColumnsNormalized = statsColumns
      .filter(s => isMetricEnabled(s.prop))
      .map(s => {
        return {
          id: StatsColumnsDictionary[s.prop] || s.prop,
          isSortable: true,
          key: StatsColumnsDictionary[s.prop] || s.prop,
          prop: StatsColumnsDictionary[s.prop] || s.prop,
          style: {
            ...defaultStyles,
            width: 120
          },
          title: s.cpy,
          type: s.type,
          units: s.units,
          visible: s.visible
        };
      });
    columns.push(...statsColumnsNormalized);
  }

  const campaignIds = campaigns.map(c => c.id);

  const groupColumns: React.ComponentProps<typeof KATable>['columns'] = groupOptions.map(variable => ({
    id: variable.id,
    isSortable: true,
    key: getWithSuffix(variable.id),
    style: {
      ...defaultStyles,
      width: 200
    },
    title: variable.label.startsWith('cartoPyramid')
      ? `${formatMessage({
          defaultMessage: 'Cartography - ',
          id: 'reportingStats.cpyFiltercartoPyramid'
        })} ${cartographyDictionaryTagConfigsData?.find(op => variable.label.includes(op.id!))?.name || variable.label}`
      : formatMessage({
          defaultMessage: capitalize(variable.label),
          id: `reportingStats.cpyFilter${capitalizeFirstLetter(variable.label)}`
        }),
    visible: true,
    width: 120
  }));

  if (includeDateGrouping) {
    const temporalColumns = DATE.map(d => ({
      id: d,
      isSortable: true,
      key: getWithSuffix(d),
      prop: d,
      style: {
        ...defaultStyles,
        width: 200
      },
      title: formatMessage({
        defaultMessage: capitalize(d),
        id: `reporting.cpy${capitalize(d)}`
      }),
      visible: true
    }));
    groupColumns.push(...temporalColumns);
  }

  const getOpts = () => {
    const currencyOptions = {
      baseCurrency: currency.base,
      currency: currency.current
    };
    if (staticMode) {
      return {
        currencyOptions
      };
    }

    return {
      ...opts,
      currencyOptions,
      includeUniques: true,
      viewabilityReport: false
    };
  };

  const getGroupOptionsParams = (groupsOptions: string[], pageIndex: number, pageSize: number) => {
    if (staticMode && !groupsOptions.includes(DEFAULT_STATIC_MODE_DIMENSION)) {
      groupsOptions = [DEFAULT_STATIC_MODE_DIMENSION, ...groupsOptions];
    }
    return groupsOptions.map(g => ({
      field: g,
      from: pageIndex * pageSize,
      others: false,
      size: pageSize
    }));
  };

  const getSortOptionsParams = (groupsOptions: string[], orderBy?: string, orderDirection?: string) => {
    if (defaultGroups.length && isFirstRender) {
      setState(prevState => ({
        ...prevState,
        isFirstRender: false
      }));
      return {
        metric: defaultGroups[0],
        order: 'desc'
      };
    }
    if (staticMode) {
      return {
        metric: orderBy ?? DEFAULT_STATIC_MODE_DIMENSION,
        order: orderDirection ?? 'desc'
      };
    }
    if (!groupsOptions.length) {
      return undefined;
    }
    return {
      // avoid crashing, RBA doesn't support multiple group order by sdate column
      metric: orderBy === groupsOptions[0] && groupsOptions.length > 1 ? groupsOptions[1] : orderBy,
      order: orderDirection
    };
  };

  const getDimensions = (groupsOptions: string[]): string[] | undefined => {
    if (!staticMode) {
      return undefined;
    }
    if (!groupsOptions.length || (groupsOptions.length === 1 && groupsOptions[0] === DEFAULT_STATIC_MODE_DIMENSION)) {
      return [];
    }
    return groups.filter(g => g !== DEFAULT_STATIC_MODE_DIMENSION);
  };

  const getItemId = (item: Record<string, any>, groupsArgs: string[]) => {
    if (staticMode && !groups.length) {
      return undefined;
    }
    return groupsArgs.map(key => item[key].toString()).join('_');
  };

  const fetchFunction: React.ComponentProps<typeof KATable>['fetchFunction'] = async ({
    limit = 10,
    orderBy,
    orderDirection,
    page = 0,
    params
  }) => {
    const { dateInterval = {}, filters: filtersArgs = {}, groups: groupsArgs = [] } = params || {};

    const dataFees = campaigns
      .map(campaign => (Array.isArray(campaign.dataFees) ? campaign.dataFees.map(fee => `dataFees.${fee}`) : []))
      .reduce((acc, val) => acc.concat(val), []);

    const body = {
      opts: getOpts(),
      params: {
        dateInterval: {
          beginDate: date.begin,
          endDate: date.end,
          timezone: date.timezone,
          ...dateInterval
        },
        ...(!staticMode && { excludeTypes: statsPrefs.excludeTypes.join(',') }),
        dimensions: getDimensions(groupsArgs),
        filters: {
          ...filters,
          ...filtersArgs
        },
        groupOptions: getGroupOptionsParams(groupsArgs, page, limit),
        outputMetrics: getTableOutputMetrics(
          type,
          columns.map(m => m.id),
          dataFees
        ).sort(),
        sortOptions: getSortOptionsParams(groupsArgs, orderBy, orderDirection)
      }
    };

    const data = await getPaginatedReportingStats(user.id, campaignIds, body);
    return {
      items: data.results.map((i: Record<string, any>) => ({
        ...i,
        id: getItemId(i, groupsArgs)
      })) as any[],
      total: data.metadata.groupOptions[data.metadata.groupOptions.length - 1]?.totalSize ?? 1
    };
  };

  const formatFn: React.ComponentProps<typeof KATable>['format'] = ({ column, value = '0' }): string | undefined => {
    const columnConfig = columns.find(c => c.id === column.id);
    const isDateDimension = DATE_DIMENSIONS.includes(column.id);
    const isTimePartDimension = TIME_PART_DIMENSIONS.includes(column.id);
    if (columnConfig?.type) {
      switch (columnConfig.type) {
        case 'currency':
          return Formatters.currency(value, currency.info);
        case 'float':
          return columnConfig?.units === '%'
            ? Formatters.percentage(value, {
                country,
                fixedDecimals: 2
              })
            : Formatters.number(value, {
                country,
                fixed: 2
              });
        case 'integer':
          return Formatters.number(value, {
            country,
            fixed: 0
          });
        default:
          return value;
      }
    }
    if (isDateDimension) {
      return Formatters.date(value, column.id || 'day');
    }
    if (isTimePartDimension) {
      return value;
    }
  };

  const handleGroupChanges = (changedGroups: string[]) => {
    let newGroups = staticMode ? staticReports.find(sr => sr.id === changedGroups[0])?.filters || [] : changedGroups;
    if (staticMode && newGroups.length) {
      newGroups = [DEFAULT_STATIC_MODE_DIMENSION, ...newGroups];
    }
    onGroupsChange(newGroups);
    setState(prevState => ({
      ...prevState,
      groups: newGroups
    }));
  };

  const groupsForColumns =
    staticMode && !groups.length ? [getWithSuffix(DEFAULT_STATIC_MODE_DIMENSION)] : groups.map(g => getWithSuffix(g));

  const orderedColumns = [
    ...groupsForColumns.map(g => groupColumns.filter(gC => gC.key === g)[0]).filter(Boolean),
    ...columns
  ].map(column =>
    column.id === 'data_fee'
      ? {
          ...column,
          tooltipConfig: {
            config: {
              arrow: false,
              component: (
                <DataFeesTooltip
                  campaigns={campaigns as any[]}
                  currencyInfo={currency.info}
                  id="statsTableDataFeeTooltip"
                />
              ),
              forcePlacement: false,
              interactions: ['click'],
              padding: '0',
              placement: 'top',
              portal: true,
              strategy: 'absolute'
            },
            icon: 'fas fa-question-circle'
          }
        }
      : column
  );

  const allAvailableDimensions = [...getTimeOptions(), ...getGroupOptions()];
  let selectedOptions: { id: string; label: string }[] = [];
  if (staticMode) {
    selectedOptions = getGroupOptions().filter(go => go.label === DEFAULT_REPORT_NAME);
  } else {
    selectedOptions = defaultGroups.map(groupEntry => {
      return {
        id: getWithSuffix(groupEntry),
        label: allAvailableDimensions.find(({ id }) => id === groupEntry)!.label
      };
    });
  }

  const tableGroups = groups.length ? groups.map(g => getWithSuffix(g)) : undefined;

  return (
    <div className="StatsTable">
      <div className="reportingShortcuts__element" id={TABLE_TYPE_ID[type]} />
      <div className="reportingInsightsGroupItem__containerHeader">
        <div className="h2 fontRegular">
          {formatMessage({
            defaultMessage: capitalizeFirstLetter(type),
            id: `reporting.cpyTable${capitalizeFirstLetter(type)}Title`
          })}
        </div>
      </div>
      <div className="boxPanel">
        <div className="statsTable__reportingFiltersContent">
          <ReportFilters
            defaultSelectedOptions={selectedOptions}
            groupOptions={getGroupOptions()}
            numberOfSelectableOptions={staticMode ? DIMENSIONS_TO_SELECT_WHEN_STATIC : undefined}
            onChange={handleGroupChanges}
            timeOptions={getTimeOptions()}
          />
        </div>
        <KATable
          columns={orderedColumns}
          enableSortingMode
          fetchConfig={{
            keyPrefix: type
          }}
          fetchFunction={fetchFunction as any}
          format={formatFn}
          groups={tableGroups}
          id={`statsTable-${type}`}
          internalPagination={{
            enabled: true,
            limit: 10
          }}
          onColumnVisibilityChange={onColumnVisibilityChange}
          showColumnsFilter
          treeColumnDefaultConfig={{
            cellStyle: {
              ...defaultStyles,
              overflow: 'hidden'
            },
            style: {
              overflow: 'auto',
              width: 400
            }
          }}
          visibleColumns={visibleColumns}
        />
      </div>
    </div>
  );
}

StatsTable.displayName = 'StatsTable';
export default StatsTable;
